import { Controller } from "react-hook-form";
import { FormLabel } from "../FormLabel";
import { Select } from "../Select";
import { FormErrorLabel } from "../FormErrorLabel";

const FormSelect = ({
  name,
  label,
  control,
  error,
  isRequired,
  options,
  onChange: onSelectChange,
  value,
  isLoading,
  ...rest
}: any) => {
  // Helper function to check if value exists in options
  // Handles both string and number comparisons
  const getValidValue = (val: any, opts: any[]) => {
    // If no value or empty options, return undefined
    if (val === null || val === undefined || val === "" || !opts?.length) {
      return undefined;
    }

    // Convert both value and option values to strings for comparison
    const stringVal = String(val).trim();

    // Check if value exists in options (comparing as strings)
    const matchingOption = opts.find((opt) => {
      if (opt.value === null || opt.value === undefined) return false;
      return String(opt.value).trim() === stringVal;
    });

    // Return the original option value (not necessarily string) if found
    return matchingOption ? String(matchingOption.value) : undefined;
  };

  // Check if we should render the Select component
  // Don't render if: value is set but options are not loaded yet
  const shouldRenderSelect = (currentValue: any) => {
    const hasValue =
      currentValue !== null &&
      currentValue !== undefined &&
      currentValue !== "";
    const hasOptions = options && Array.isArray(options) && options.length > 0;

    // If there's a value but no options, don't render (wait for options to load)
    if (hasValue && !hasOptions) {
      return false;
    }

    // Otherwise, render if options are available or if there's no value
    return hasOptions || !hasValue;
  };

  const selectElement = (
    <Select
      options={options}
      value={getValidValue(value, options)}
      {...rest}
      onChange={(val: any) => onSelectChange && onSelectChange(val)}
    />
  );

  return (
    <div className="relative flex flex-col items-start gap-1">
      {label && (
        <FormLabel>
          {label} <span className="text-red-700">{isRequired ? "*" : ""}</span>
        </FormLabel>
      )}

      {/* ✅ If control exists → use Controller, else fallback to uncontrolled Select */}
      {control ? (
        <Controller
          name={name}
          control={control}
          render={({ field }) => {
            // Check if we should render the Select
            if (!shouldRenderSelect(field.value)) {
              // Show loading state or placeholder while waiting for options
              return (
                <div className="w-full h-[40px] rounded-[4px] border border-gray-300 bg-gray-50 flex items-center px-3 text-gray-500">
                  {isLoading ? "Loading..." : "Please wait..."}
                </div>
              );
            }

            // Calculate valid value - will recalculate when field.value or options change
            const validValue = getValidValue(field.value, options);

            return (
              <Select
                options={options}
                {...rest}
                value={validValue}
                onChange={(val: any) => {
                  onSelectChange && onSelectChange(val);
                  field.onChange(val);
                }}
                onBlur={field.onBlur}
                name={field.name}
                ref={field.ref}
              />
            );
          }}
        />
      ) : shouldRenderSelect(value) ? (
        selectElement
      ) : (
        <div className="w-full h-[40px] rounded-[4px] border border-gray-300 bg-gray-50 flex items-center px-3 text-gray-500">
          {isLoading ? "Loading..." : "Please wait..."}
        </div>
      )}

      {error && <FormErrorLabel>{error}</FormErrorLabel>}
    </div>
  );
};

export default FormSelect;
